import { jsx } from 'react/jsx-runtime';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { createContext, useContext, useMemo } from 'react';
import { useApp } from './AppContext.js';

const OrganizationContext = createContext(undefined);
/**
 * Provider that fetches organization data from the backend API
 */
function OrganizationProviderInner({ children }) {
    const { auth, getApiUrl } = useApp();
    const { data: dbOrgData, isLoading: dbLoading } = useQuery({
        queryKey: ['Organization', 'current'],
        queryFn: async () => {
            try {
                const baseURL = getApiUrl();
                if (!baseURL) {
                    throw new Error('API base URL is not defined');
                }
                const headers = {
                    'Content-Type': 'application/json',
                };
                if (auth?.getToken) {
                    const token = await auth.getToken();
                    if (token) {
                        headers.Authorization = `Bearer ${token}`;
                    }
                }
                const response = await axios.get(`${baseURL}/organizations/current`, { headers });
                return response.data.data;
            }
            catch (error) {
                console.error('Error fetching organization from database:', error);
                return null;
            }
        },
        enabled: !!auth?.isAuthenticated,
        staleTime: 5 * 60 * 1000,
    });
    const value = useMemo(() => {
        const dbOrganization = dbOrgData || null;
        const organizationSelected = !!dbOrganization;
        const isInternal = dbOrganization?.isInternal ?? false;
        const isLoading = (auth?.isLoading ?? false) || (auth?.isAuthenticated ? dbLoading : false);
        const organization = dbOrganization
            ? {
                id: dbOrganization.id,
                name: dbOrganization.name,
                slug: dbOrganization.slug,
                imageUrl: dbOrganization.imageUrl,
            }
            : null;
        return {
            dbOrganization,
            organizationSelected,
            isInternal,
            isLoading,
            organization,
        };
    }, [dbOrgData, auth?.isLoading, auth?.isAuthenticated, dbLoading]);
    return jsx(OrganizationContext.Provider, { value: value, children: children });
}
/**
 * Organization context provider. Fetches and exposes the current organization.
 * @param props - Component props including children
 * @returns JSX element providing organization context
 */
function OrganizationProvider({ children }) {
    return jsx(OrganizationProviderInner, { children: children });
}
/**
 * Hook to access the current organization context
 * @returns OrganizationContextValue with organization data and status flags
 * @throws Error if used outside of OrganizationProvider
 */
function useOrganization() {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
}

export { OrganizationProvider, useOrganization };
//# sourceMappingURL=OrganizationContext.js.map
